<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_CHARGE')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="chargeRef"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="chargeList"
      responsive
      selectable
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <!-- Column: Charge Name -->
      <template #cell(chargeName)="data">
        {{ data.value }}
      </template>

      <!-- Column: amount 1 -->
      <template #cell(amount1)="data">
        {{ toAmount(data.value ) }}
      </template>

      <!-- Column: amount 2 -->
      <template #cell(amount2)="data">
        <p class="mb-0">
          {{ toAmount(data.value ) }}
        </p>
      </template>

      <template #cell(dateCreated)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge></template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_CHARGE')"
              @click="onSelectChargeItem(data.item, $event.target, 'edit', data.index)"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t('Modify') }}</span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectChargeItem(data.item, $event.target, 'view', data.index)">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">{{ $t('View') }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Active' ? 'DEACTIVATE_CHARGE' : 'ACTIVATE_CHARGE')"
              @click="chargeStatus(data.item, $event.target, data.index)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ data.item.status === 'Active' ? $t('Deactivate') : $t('Activate') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      size="lg"
      no-close-on-backdrop
      :title="modalTitle"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="chargeRules">
        <b-form @submit.prevent="handleSubmit">
          <b-row class="match-height">
            <!-- Charge Name -->
            <b-col md="12">
              <InputField
                id="charge-name"
                v-model="chargeFormData.name"
                :label="$t('Charge Name')"
                :placeholder="$t('Enter name')"
                type="text"
                :error-name="$t('Charge Name')"
                rules="required"
                for="charge-name"
                :readonly="isReadonly"
                onpaste="return false;"
                :is-allow-special-characters="false"
              />
            </b-col>
            <!-- Charge Name -->

          </b-row>

          <b-row class="match-height">
            <!-- Amount 1 -->
            <b-col md="6">
              <InputField
                id="amount-1"
                v-model.number="chargeFormData.amount1"
                :label="$t('Amount 1')"
                :placeholder="$t('Enter amount')"
                :error-name="$t('Amount 1')"
                rules="required|positive"
                for="amount-1"
                type="number"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Amount 1 -->
            <!-- Income GL for Amount 1 -->
            <b-col md="6">
              <SelectField
                id="income-gl"
                v-model="chargeFormData.amount1IncomeGL"
                :label="$t('Income GL for Amount 1')"
                :placeholder="$t('Select')"
                :options="incomeGlOptions"
                :reduce="option => option.value"
                :error-name="$t('Income GL for Amount 1')"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Income GL for Amount 1 -->
          </b-row>

          <b-row class="match-height align-items-center">
            <!-- Amount 2 -->
            <b-col md="6">
              <InputField
                id="amount-2"
                v-model.number="chargeFormData.amount2"
                type="number"
                :label="$t('Amount 2')"
                :placeholder="$t('Enter amount')"
                :error-name="$t('Amount 2')"
                rules="positive"
                for="amount-2"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Amount 2 -->

            <!-- Stationery GL for Amount 2 -->
            <b-col md="6">
              <SelectField
                id="stationery-gl"
                v-model="chargeFormData.amount2StationeryGLAccount"
                :label="$t('Stationery GL for Amount 2')"
                :placeholder="$t('Select')"
                :options="stationeryGlOptions"
                :reduce="option => option.value"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Stationery GL for Amount 2 -->
          </b-row>
          <b-row class="match-height align-items-center">
            <!-- Consolidate both amounts  -->
            <b-col md="8">
              <b-form-group class="pt-3">
                <b-form-checkbox
                  v-model="chargeFormData.consolidateBothAmountsInCustomerAccount"
                  checked="false"
                  name="check-button"
                  switch
                  inline
                  :disabled="isReadonly"
                >
                  {{ $t('Consolidate both amounts in Customer\'s Account?') }}
                </b-form-checkbox>
              </b-form-group>
            </b-col>
            <!-- Consolidate both amounts  -->
          </b-row>

        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="md"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchCharge"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Name -->
        <b-col md="12">
          <InputField
            id="name"
            v-model="filter.name"
            :label="$t('Name')"
            :placeholder="$t('Enter name')"
            type="text"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Name -->

        <!-- Status -->
        <b-col md="12">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Status')"
            :reduce="option=>option"
            :placeholder="$t('Select')"
            :options="statusOptions"
            item="label"
          />
        </b-col>
        <!-- Status -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BBadge,
  BModal, BFormGroup, BForm, BFormCheckbox, BSpinner, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required, positive } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions, mapGetters } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import { toAmount } from '@/helpers/formatAmount'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../forms/InputField.vue'
import SelectField from '../../forms/SelectField.vue'
import { toDateWithYear } from '../../../helpers/formatDate'
import getStatusColor from '../../../helpers/getStatusColor'
import { transformBoolean } from '../../../helpers/transformers'
import Button from '../../components/button/Button.vue'
import { accountPermissions } from '../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BBadge,
    TablePagination,
    BModal,
    BFormGroup,
    BForm,
    BSpinner,
    BFormCheckbox,
    ValidationObserver,
    SelectField,
    InputField,
    BDropdown,
    BDropdownItem,
    FullScreenSpinner,
    Button,
    TableEntries,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      sortBy: 'chargeName',
      required,
      toAmount,
      getStatusColor,
      positive,
      sortDesc: false,
      buttonLabel: this.$t('Save'),
      modalTitle: this.$t('Create New Charge'),
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'name', sortable: true, label: 'Name' },
        { key: 'amount1', sortable: true, label: 'Amount 1' },
        { key: 'amount2', sortable: true, label: 'Amount 2' },
        { key: 'consolidateBothAmountsInCustomerAccount', sortable: true, label: 'Consolidate Both Amounts' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      chargeFormData: {
        name: '',
        amount1: '',
        amount2: '',
        amount1IncomeGL: '',
        amount2StationeryGLAccount: '',
        consolidateBothAmountsInCustomerAccount: false,
      },
      filter: {
        name: '',
        status: '',
        sortColumn: '',
      },
      chargeList: [],
      incomeGlOptions: [],
      stationeryGlOptions: [],
    }
  },
  computed: {
    ...mapGetters({
      // incomeGlOptions: 'account/incomeGlByParentCategoryOptions',
      // stationeryGlOptions: 'account/stationaryGL',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchCharge()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchCharge()
    },
  },
  mounted() {
    this.fetchCharge()
    this.fetchIncomeGLByParentCategory()
    this.fetchStationaryAccount()
  },
  methods: {
    ...mapActions({
      getCharge: 'account/getChargeList',
      updateCharge: 'account/updateChargeAction',
      createCharge: 'account/createChargeAction',
      updateChargeStatus: 'account/updateStatus',
      getIncomeGLByParentCategory: 'account/getIncomeGLByParentCategory',
      getStationaryAccount: 'account/getStationaryAccount',
    }),
    async chargeStatus(items) {
      const { id, name } = items
      const actionType = items.status === 'Active' ? this.chargeFormData.status = 'InActive' : this.chargeFormData.status = 'Active'
      const action = items.status === 'Active' ? this.$t('Deactivate') : this.$t('Activate')
      const permission = items.status === 'Active' ? accountPermissions.DEACTIVATE_CHARGE : accountPermissions.ACTIVATE_CHARGE

      const updateStatus = async () => {
        try {
          this.showSpinner = true
          const statusValues = {
            id,
            name,
          }
          const response = await this.updateChargeStatus({
            statusValues, actionType, routeName: 'Charge', permission,
          })
          if (response.status === 200) {
            this.fetchCharge()
            const successMessage = action === this.$t('Activate')
              ? this.$t('Your request to Activate Charge Management was successful')
              : this.$t('Your request to Deactivate Charge Management was successful')
            this.alertSuccess({ text: successMessage })
            this.showSpinner = false
          }
        } catch (error) {
          this.alertError({
            text: action === this.$t('Activate')
              ? this.$t(`Your request to Activate Charge Management failed. Reason: ${error}`)
              : this.$t(`Your request to Deactivate Charge Management failed. Reason: ${error}`),
          })
        } finally {
          this.showSpinner = false
        }
      }
      const actionText = action === this.$t('Activate')
        ? this.$t('Are you sure you want to Activate this Charge Management?')
        : this.$t('Are you sure you want to Deactivate this Charge Management?')
      this.confirm(updateStatus, { text: actionText })
    },
    handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.chargeRules.validate().then(async success => {
        if (success) {
          const actionText = this.buttonLabel === this.$t('Save')
            ? this.$t('Are you sure you want to Save this Charge Management?')
            : this.$t('Are you sure you want to Update this Charge Management?')
          this.confirm(this.chargeManagementAction, { text: actionText })
        }
      })
    },
    async chargeManagementAction() {
      try {
        this.isLoading = true
        const response = this.buttonLabel === this.$t('Save')
          ? await this.createCharge({ ...this.chargeFormData, amount2: this.chargeFormData.amount2 === '' ? 0 : this.chargeFormData.amount2 })
          : await this.updateCharge({ ...this.chargeFormData, amount2: this.chargeFormData.amount2 === '' ? 0 : this.chargeFormData.amount2 })

        const { status } = await response
        if (status === 200) {
          this.fetchCharge()
          this.hideModal('modal-lg')
          const successMessage = this.buttonLabel === this.$t('Save')
            ? this.$t('Your request to Save Charge Management was successful')
            : this.$t('Your request to Update Charge Management was successful')
          this.alertSuccess({ text: successMessage })
        }
      } catch (error) {
        this.alertError({
          text: this.buttonLabel === this.$t('Save')
            ? this.$t(`Your request to Save Charge Management failed. Reason: ${error}`)
            : this.$t(`Your request to Update Charge Management failed. Reason: ${error}`),
        })
      } finally {
        this.isLoading = false
      }
    },
    async fetchCharge() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getCharge({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.chargeList = data.data.map(items => ({
            ...items,
            consolidateBothAmountsInCustomerAccount: transformBoolean(items.consolidateBothAmountsInCustomerAccount),
          }))
          this.totalCount = data.recordCount
        } else {
          this.chargeList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async onSelectChargeItem(items, activity, val, index) {
      try {
        this.$refs.chargeRef.selectRow(index)
        this.chargeFormData.name = items.name
        this.chargeFormData.amount1 = items.amount1
        this.chargeFormData.amount1IncomeGL = items.amount1IncomeGL
        this.chargeFormData.amount2 = items.amount2
        this.chargeFormData.amount2StationeryGLAccount = items.amount2StationeryGLAccount
        this.chargeFormData.consolidateBothAmountsInCustomerAccount = items.consolidateBothAmountsInCustomerAccount !== 'No'
        this.chargeFormData.id = items.id
        this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
        this.modalTitle = val === 'edit' ? this.$t('Edit Charge Management') : this.$t('View Charge Management')
        this.buttonLabel = this.$t('Update')
        this.$root.$emit('bv::show::modal', 'modal-lg')
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async fetchIncomeGLByParentCategory() {
      try {
        const response = await this.getIncomeGLByParentCategory({ permission: accountPermissions.VIEW_CHARGE })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.incomeGlOptions = data.data.reduce((acc, curr) => [...acc, { label: `${curr.name} - ${curr.code}`, value: curr.code.toString() }], [{ label: 'Select', value: 'Select' }])
        } else {
          this.incomeGlOptions = []
        }
      } catch (error) {
        this.handleError(error)
      }
    },
    async fetchStationaryAccount() {
      try {
        const response = await this.getStationaryAccount({ permission: accountPermissions.VIEW_CHARGE })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.stationeryGlOptions = data.data.reduce((acc, curr) => [...acc, { label: `${curr.name} - ${curr.code}`, value: curr.code.toString() }], [{ label: 'Select', value: 'Select' }])
        } else {
          this.stationeryGlOptions = []
        }
      } catch (error) {
        this.handleError(error)
      }
    },
    resetModal() {
      this.chargeFormData.name = ''
      this.chargeFormData.amount1 = ''
      this.chargeFormData.amount1IncomeGL = ''
      this.chargeFormData.amount2 = ''
      this.chargeFormData.amount2StationeryGLAccount = ''
      this.chargeFormData.consolidateBothAmountsInCustomerAccount = false
      this.chargeFormData.id = ''
      this.buttonLabel = this.$t('Save')
      this.modalTitle = this.$t('Create Charge Management')
      this.$refs.chargeRef.clearSelected()
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchCharge()
    },
  },
}
</script>
